import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IPrefillValue, PrefillValueModel } from '../models/prefill-value.model';
import { IPrefill, PrefillModel } from '../models/prefill.model';
import { IPresignFileResponse, PresignFileResponse } from '../models/presign-file.model';
import { IServerResponse } from '../models/server-response.model';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { UtilService } from './util.service';

/** Body de `POST /prefills`. */
export interface IPrefillCreateRequest {
  templateId: string;
  name: string;
  /** MIME del archivo a subir. `null` = no se sube nada en esta llamada. */
  contentType: string | null;
}

/** Body de `PUT /prefills/{prefillId}`. OJO: `templateId` NO se actualiza. */
export interface IPrefillUpdateRequest {
  name: string;
  status: boolean;
  contentType: string | null;
}

/** Body de `POST /prefills/{prefillId}/values`. */
export interface IPrefillValueCreateRequest {
  templateFieldId: string;
  value: string;
}

/** Body de `PUT /prefills/{prefillId}/values/{prefillValueId}`. */
export interface IPrefillValueUpdateRequest {
  value: string;
}

/**
 * Resultado de crear / actualizar un prerellenado: el registro y, cuando se
 * mando `contentType`, la URL firmada para subir el archivo a S3.
 */
export interface IPrefillMutationResult {
  prefill: PrefillModel;
  presignFile: PresignFileResponse | null;
}

/**
 * Catalogo de prerellenados y de sus valores.
 *
 * Los valores NO tienen GET propio: llegan dentro de `prefill.values`, cada uno
 * con su `templateField`. El listado no tiene paginacion y viene ordenado por
 * `name asc`.
 */
@Injectable({ providedIn: 'root' })
export class PrefillService {
  private readonly http = inject(HttpClient);
  private readonly spinner = inject(SpinnerService);
  private readonly toast = inject(ToastService);
  private readonly util = inject(UtilService);
  private readonly urlApi = `${environment.urlApi}/prefills`;

  readonly loading = signal(false);
  readonly prefills = signal<PrefillModel[]>([]);
  readonly prefill = signal<PrefillModel | null>(null);

  /**
   * `false` mientras NO se sabe todavia si hay datos: la primera peticion no
   * ha terminado. La vista pinta esqueletos hasta que pase a `true`; sin esta
   * senal el primer render enseñaria el estado vacio con la lista aun
   * cargando.
   */
  readonly prefillsLoaded = signal(false);

  /** Mensaje del ultimo fallo de carga del listado, o `null` si salio bien. */
  readonly prefillsError = signal<string | null>(null);

  /** Mensaje del ultimo fallo al cargar UN prerellenado, o `null` si salio bien. */
  readonly prefillError = signal<string | null>(null);

  async getPrefills(): Promise<PrefillModel[]> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ prefills: IPrefill[] }>>(this.urlApi),
      );

      if (response.statusCode !== 200) {
        this.prefillsError.set(response.message);
        this.toast.error(response.message);
        return [];
      }

      const prefills = (response.data?.prefills ?? []).map((item) => new PrefillModel(item));
      this.prefills.set(prefills);
      this.prefillsError.set(null);
      return prefills;
    } catch (error) {
      const message = this.util.errorMessage(error, 'No se pudieron cargar los prerellenados.');
      this.prefillsError.set(message);
      this.toast.error(message);
      return [];
    } finally {
      this.loading.set(false);
      this.prefillsLoaded.set(true);
    }
  }

  async getPrefill(prefillId: string): Promise<PrefillModel | null> {
    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.http.get<IServerResponse<{ prefill: IPrefill }>>(`${this.urlApi}/${prefillId}`),
      );

      if (response.statusCode !== 200) {
        this.prefillError.set(response.message);
        this.toast.error(response.message);
        this.prefill.set(null);
        return null;
      }

      const prefill = response.data?.prefill ? new PrefillModel(response.data.prefill) : null;
      this.prefill.set(prefill);
      this.prefillError.set(null);
      return prefill;
    } catch (error) {
      const message = this.util.errorMessage(error, 'No se pudo cargar el prerellenado.');
      this.prefillError.set(message);
      this.toast.error(message);
      this.prefill.set(null);
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  /** POST devuelve 201. Un 400 significa plantilla inexistente o inactiva. */
  async createPrefill(request: IPrefillCreateRequest): Promise<IPrefillMutationResult | null> {
    this.spinner.show('Creando prerellenado...');
    try {
      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ prefill: IPrefill; presignFile?: IPresignFileResponse }>>(
          this.urlApi,
          request,
        ),
      );

      if (response.statusCode !== 201 || !response.data?.prefill) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return {
        prefill: new PrefillModel(response.data.prefill),
        presignFile: response.data.presignFile
          ? new PresignFileResponse(response.data.presignFile)
          : null,
      };
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo crear el prerellenado.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  async updatePrefill(
    prefillId: string,
    request: IPrefillUpdateRequest,
  ): Promise<IPrefillMutationResult | null> {
    this.spinner.show('Guardando prerellenado...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ prefill: IPrefill; presignFile?: IPresignFileResponse }>>(
          `${this.urlApi}/${prefillId}`,
          request,
        ),
      );

      if (response.statusCode !== 200 || !response.data?.prefill) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return {
        prefill: new PrefillModel(response.data.prefill),
        presignFile: response.data.presignFile
          ? new PresignFileResponse(response.data.presignFile)
          : null,
      };
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo guardar el prerellenado.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /** DELETE es soft-delete: deja `status = false`. */
  async deletePrefill(prefillId: string): Promise<PrefillModel | null> {
    this.spinner.show('Desactivando prerellenado...');
    try {
      const response = await firstValueFrom(
        this.http.delete<IServerResponse<{ prefill: IPrefill }>>(`${this.urlApi}/${prefillId}`),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.prefill ? new PrefillModel(response.data.prefill) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo desactivar el prerellenado.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /**
   * POST devuelve 201. Un 400 significa que el campo no pertenece a la
   * plantilla del prerellenado.
   *
   * OJO: el API NO impide dos valores para el mismo `templateFieldId`. El
   * duplicado se evita en el cliente ofreciendo solo los campos sin valor.
   */
  async createPrefillValue(
    prefillId: string,
    request: IPrefillValueCreateRequest,
  ): Promise<PrefillValueModel | null> {
    this.spinner.show('Creando valor...');
    try {
      const response = await firstValueFrom(
        this.http.post<IServerResponse<{ prefillValue: IPrefillValue }>>(
          `${this.urlApi}/${prefillId}/values`,
          request,
        ),
      );

      if (response.statusCode !== 201) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.prefillValue ? new PrefillValueModel(response.data.prefillValue) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo crear el valor.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  async updatePrefillValue(
    prefillId: string,
    prefillValueId: string,
    request: IPrefillValueUpdateRequest,
  ): Promise<PrefillValueModel | null> {
    this.spinner.show('Guardando valor...');
    try {
      const response = await firstValueFrom(
        this.http.put<IServerResponse<{ prefillValue: IPrefillValue }>>(
          `${this.urlApi}/${prefillId}/values/${prefillValueId}`,
          request,
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.prefillValue ? new PrefillValueModel(response.data.prefillValue) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo guardar el valor.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /**
   * OJO: los valores de prerellenado son BORRADO FISICO, no soft-delete. La
   * fila desaparece y no hay forma de recuperarla; la confirmacion tiene que
   * decirlo con todas sus letras.
   */
  async deletePrefillValue(
    prefillId: string,
    prefillValueId: string,
  ): Promise<PrefillValueModel | null> {
    this.spinner.show('Eliminando valor...');
    try {
      const response = await firstValueFrom(
        this.http.delete<IServerResponse<{ prefillValue: IPrefillValue }>>(
          `${this.urlApi}/${prefillId}/values/${prefillValueId}`,
        ),
      );

      if (response.statusCode !== 200) {
        this.toast.error(response.message);
        return null;
      }

      this.toast.success(response.message);
      return response.data?.prefillValue ? new PrefillValueModel(response.data.prefillValue) : null;
    } catch (error) {
      this.toast.error(this.util.errorMessage(error, 'No se pudo eliminar el valor.'));
      return null;
    } finally {
      this.spinner.hide();
    }
  }

  /**
   * Sube el archivo a la URL firmada de S3.
   *
   * CRITICO: a S3 NO puede viajar el header `Authorization`. `authInterceptor`
   * hace early-return para toda URL que no empiece por `environment.urlApi`,
   * asi que este PUT sale limpio con el HttpClient normal. El `Content-Type`
   * debe ser EXACTAMENTE el `contentType` que se mando al API. La URL caduca a
   * los 300 s.
   */
  async uploadFile(presignFile: PresignFileResponse, file: File): Promise<boolean> {
    this.spinner.show('Subiendo imagen...');
    try {
      await firstValueFrom(
        this.http.put(presignFile.uploadUrl, file, {
          headers: new HttpHeaders({ 'Content-Type': file.type }),
          responseType: 'text',
        }),
      );
      return true;
    } catch (error) {
      this.toast.error(
        this.util.errorMessage(error, 'No se pudo subir la imagen. Intenta de nuevo.'),
      );
      return false;
    } finally {
      this.spinner.hide();
    }
  }
}
